import { useRef, useState, type ChangeEvent } from "react";
import { ACCEPT_ATTR, fileExt, fileToMarkdown, uploadStatusLabel } from "./fileToMarkdown";

interface FileItem {
  id: number;
  name: string;
  status: "pending" | "done" | "error";
  message: string;
}

interface Props {
  /** Called with the markdown produced for each file once it's been parsed. */
  onConverted: (markdown: string, fileName: string) => void;
  /** Optional element id so legacy CSS selectors keep working in tests. */
  inputId?: string;
  hint?: string;
  ariaLabel?: string;
}

let fileCounter = 0;

/**
 * Multi-file upload row. Files are converted client-side (TXT/PDF/DOCX) or via
 * the server endpoint (audio/image/scanned PDF) and the resulting markdown is
 * forwarded to the parent so it can append it to the relevant textarea — same
 * UX as the legacy app.
 */
export function FileUpload({ onConverted, inputId, hint, ariaLabel }: Props) {
  const [items, setItems] = useState<FileItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  async function process(file: File) {
    const id = ++fileCounter;
    const ext = fileExt(file.name);
    setItems((prev) => [
      ...prev,
      { id, name: file.name, status: "pending", message: uploadStatusLabel(ext) },
    ]);
    try {
      const md = await fileToMarkdown(file);
      onConverted(md.trim() || "(kein Text extrahiert)", file.name);
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, status: "done", message: "Fertig" } : it)),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Fehler";
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, status: "error", message } : it)),
      );
    }
  }

  function onChange(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (inputRef.current) inputRef.current.value = "";
    for (const f of files) {
      const ext = fileExt(f.name);
      if (!ACCEPT_ATTR.split(",").some((accepted) => accepted === "." + ext)) continue;
      void process(f);
    }
  }

  return (
    <>
      <div className="upload-area">
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          onChange={onChange}
          aria-label={ariaLabel ?? "Datei hochladen"}
        />
        {hint && <span className="upload-hint">{hint}</span>}
      </div>
      {items.length > 0 && (
        <ul className="file-list" aria-live="polite">
          {items.map((it) => (
            <li key={it.id} className={`file-${it.status}`}>
              <span className="file-name">{it.name}</span>
              <span className="file-status">{it.message}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
